import React, { forwardRef, type ReactNode } from 'react'
import { Pressable } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Typography } from '../../typography/Typography'
import { variantForStyleId } from '../../typography/variants'
import { chipProcessContract } from './chip-process.contract'

export const CHIP_LABEL_VARIANT = variantForStyleId(chipProcessContract.textStyleIds[0])

const LAYOUTS = chipProcessContract.props.layout.values

export type ChipProcessLayout = (typeof LAYOUTS)[number]

export type ChipProcessProps = Omit<
    React.ComponentProps<typeof Pressable>,
    'style' | 'children'
> & {
    children: ReactNode
    layout?: ChipProcessLayout
    selected?: boolean
    icon?: ReactNode
    className?: string
}

type ChipRef = React.ComponentRef<typeof Pressable>

const variantFor = (selected: boolean, layout: ChipProcessLayout) => {
    const key = `selected=${selected}, layout=${layout}`
    const variant = (
        chipProcessContract.variants as Record<
            string,
            { container: string; label: string; disabled: string; visited: string }
        >
    )[key]
    if (!variant) {
        throw new Error(`aos-ds ChipProcess: the Figma contract has no "${key}"`)
    }
    return variant
}

/**
 * Figma spells the selected axis as the strings `true` and `false`, because it
 * has no boolean variant. The contract normalises it back to a boolean, so the
 * component takes `selected` rather than `selected='true'`.
 */
export const ChipProcess = forwardRef<ChipRef, ChipProcessProps>(
    (
        {
            children,
            layout = 'horizontal',
            selected = false,
            icon,
            disabled = false,
            className,
            ...rest
        },
        ref,
    ) => {
        const variant = variantFor(selected, layout)
        return (
            <Pressable
                ref={ref}
                disabled={disabled}
                role='button'
                accessibilityState={{ selected, disabled: disabled ?? false }}
                className={twMerge(
                    layout === 'vertical' ? 'flex-col' : 'flex-row',
                    'items-center justify-center',
                    variant.container,
                    disabled ? variant.disabled : undefined,
                    className,
                )}
                {...rest}
            >
                {icon}
                <Typography variant={CHIP_LABEL_VARIANT} className={variant.label}>
                    {children}
                </Typography>
            </Pressable>
        )
    },
)

ChipProcess.displayName = 'ChipProcess'
